import { BookMarked, ExternalLink } from "lucide-react";
import { getSources } from "@/lib/constants/sources";
import type { SourceRef } from "@/lib/constants/sources";

interface SourcesAndMethodologyProps {
  /** ISO-2 country code for the page (e.g. "us"). Drives which regulators show. */
  country?: string;
  /** Topic/category label (e.g. "Tax", "retirement", "Mortgage"). */
  category?: string;
  /** Human-readable last-reviewed label, e.g. "July 2026". */
  lastUpdated?: string;
  /** Tighter spacing for use inside dense pages. */
  compact?: boolean;
  /** Inline style overrides. */
  style?: React.CSSProperties;
  /**
   * Page-specific researched citations (e.g. a blog post's sources[]).
   * Rendered FIRST, before the generic country/category regulators, and
   * deduplicated against them by URL.
   */
  extraSources?: SourceRef[];
}

/**
 * References component. Country/category regulator links give readers a
 * starting point; page-specific citations, when supplied, appear first.
 */
export default function SourcesAndMethodology({
  country,
  category,
  compact = false,
  style,
  extraSources,
}: SourcesAndMethodologyProps) {
  const registrySources = getSources(country, category);

  // Post-specific citations lead; registry regulators follow; dedup by URL.
  const seen = new Set<string>();
  const sources: SourceRef[] = [];
  for (const s of [...(extraSources ?? []), ...registrySources]) {
    if (seen.has(s.url)) continue;
    seen.add(s.url);
    sources.push(s);
  }

  if (sources.length === 0) return null;

  return (
    <section
      aria-label="Sources and references"
      style={{
        marginTop: 0,
        marginBottom: 0,
        padding: compact ? "var(--space-4)" : "var(--space-5)",
        border: "1px solid var(--border-color)",
        borderRadius: "var(--radius-lg)",
        background: "var(--bg-surface)",
        display: "flex",
        flexDirection: "column",
        gap: "var(--space-3)",
        ...style,
      }}
    >
      <div>
        {/* h2: top-level page section (was h3, which nested it under whatever
            h2 section happened to precede it in the outline). */}
        <h2
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            fontSize: "var(--text-sm)",
            fontWeight: "var(--fw-bold)",
            color: "var(--text-primary)",
            margin: "0 0 0.5rem 0",
          }}
        >
          <BookMarked size={16} style={{ color: "var(--primary)" }} /> Sources &amp; references
        </h2>
        <p style={{ fontSize: "var(--text-xs)", color: "var(--text-secondary)", margin: "0 0 0.5rem 0", lineHeight: 1.5 }}>
          These links include relevant regulators and page-specific references. Worked examples and projections may also use stated assumptions:
        </p>
        <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
          {sources.map((s) => (
            <li key={s.url}>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.35rem",
                  fontSize: "var(--text-xs)",
                  fontWeight: "var(--fw-semibold)",
                  color: "var(--primary)",
                  textDecoration: "none",
                  border: "1px solid var(--border-color)",
                  borderRadius: "var(--radius-full)",
                  padding: "0.3rem 0.7rem",
                  background: "var(--bg-main)",
                }}
              >
                {s.name} <ExternalLink size={12} />
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
